/**
 * ReferencePreviewModal
 *
 * Opens a referenced work item or document over the conversation. Clicking a
 * reference pill mid-thread should not cost the reader their place in the
 * chat, so the preview is the default and navigating away is a deliberate
 * second action.
 */

import { Component, Show } from "solid-js";
import { useNavigate } from "@solidjs/router";
import { Modal } from "../ui/Modal";
import { Badge } from "../ui/Badge";
import {
  humanise,
  itemTypeToVariant,
  statusCategoryToVariant,
} from "../../utils/status";
import type { ResolvedReference } from "../../hooks/useEntityReferences";

export interface ReferencePreviewModalProps {
  /** The resolved reference to preview. Undefined while unresolved. */
  reference: ResolvedReference | undefined;
  open: boolean;
  onClose: () => void;
}

export const ReferencePreviewModal: Component<ReferencePreviewModalProps> = (
  props,
) => {
  const navigate = useNavigate();

  const workItem = () =>
    props.reference?.kind === "work_item" ? props.reference.item : undefined;
  const docRef = () =>
    props.reference?.kind === "document" ? props.reference.doc : undefined;

  const path = () => {
    const item = workItem();
    if (item) return `/items/${item.id}`;
    const doc = docRef();
    if (doc) return `/documents/${doc.id}`;
    return undefined;
  };

  const title = () => workItem()?.title ?? docRef()?.title ?? "";
  const excerpt = () => workItem()?.excerpt ?? docRef()?.excerpt ?? null;

  const handleOpen = () => {
    const target = path();
    if (!target) return;
    navigate(target);
    props.onClose();
  };

  return (
    <Modal
      open={props.open && props.reference !== undefined}
      onClose={props.onClose}
      title={workItem() ? "Work item" : "Document"}
      size="md"
    >
      <div class="flex flex-col gap-3">
        <div class="flex flex-wrap items-center gap-2">
          <Show when={workItem()}>
            {(item) => (
              <>
                <Badge variant={itemTypeToVariant[item().item_type] ?? "task"}>
                  {item().item_type}
                </Badge>
                <span class="text-xs font-mono text-ink-3">
                  {item().display_id}
                </span>
                <Badge
                  variant={statusCategoryToVariant[item().status_category]}
                >
                  {item().status_name}
                </Badge>
                <Badge variant={item().priority}>
                  {humanise(item().priority)}
                </Badge>
              </>
            )}
          </Show>
          <Show when={docRef()}>
            {(doc) => (
              <>
                <Badge variant="epic">{humanise(doc().doc_type)}</Badge>
                <Badge variant="default">{humanise(doc().status)}</Badge>
              </>
            )}
          </Show>
        </div>

        <h3 class="text-base font-semibold text-ink break-words">{title()}</h3>

        <Show when={excerpt()}>
          {(text) => (
            <p class="text-sm text-ink-2 whitespace-pre-wrap break-words">
              {text()}
            </p>
          )}
        </Show>

        <div class="flex gap-2 justify-end pt-1">
          <button
            type="button"
            class="px-3 py-1.5 text-sm rounded bg-surface text-ink border border-line hover:bg-sunken hover:border-line-strong focus-ring"
            onClick={props.onClose}
          >
            Close
          </button>
          <button
            type="button"
            class="px-3 py-1.5 text-sm rounded bg-ink text-paper hover:bg-ink-2 focus-ring"
            onClick={handleOpen}
          >
            Open
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ReferencePreviewModal;
